import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #filterExpanded;
  #filterAnimation;
  #contentAnimation;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Preserve native/user disclosure state when a drawer hydrates or is restored.
    if (this.closest('#filters-drawer, .cl-native-drawer')) this.#filterExpanded = this.details.open;
    else this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleFilterToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#filterAnimation?.cancel();
    this.#contentAnimation?.cancel();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
    if (this.closest('#filters-drawer, .cl-native-drawer')) {
      event.preventDefault();
      this.#animateFilter(!this.#filterExpanded);
    }
  };

  #animateFilter(expanded) {
    const details = this.details;
    const content = details.querySelector('.details-content');
    if (!(content instanceof HTMLElement)) return;
    const startHeight = details.getBoundingClientRect().height;
    const startOpacity = getComputedStyle(content).opacity;
    this.#filterAnimation?.cancel();
    this.#contentAnimation?.cancel();
    this.#filterExpanded = expanded;
    this.summary.setAttribute('aria-expanded', String(expanded));
    details.open = true;
    details.style.height = 'auto';
    const endHeight = expanded ? details.getBoundingClientRect().height : this.summary.getBoundingClientRect().height;
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : expanded ? 340 : 280;
    const options = { duration, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' };
    details.style.overflow = 'hidden';
    this.#filterAnimation = details.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], options);
    this.#contentAnimation = content.animate([{ opacity: startOpacity, transform: expanded ? 'translateY(-6px)' : 'translateY(0)' }, { opacity: expanded ? 1 : 0, transform: expanded ? 'translateY(0)' : 'translateY(-6px)' }], options);
    const animation = this.#filterAnimation;
    animation.finished.then(() => {
      if (this.#filterAnimation !== animation) return;
      details.open = expanded;
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
      animation.cancel();
      this.#contentAnimation?.cancel();
      this.#filterAnimation = null;
    }).catch(() => {});
  }

  #handleFilterToggle = () => {
    // Native exclusive details groups close siblings. Cancel a sibling's pending
    // opening animation so its completion cannot reopen it and close this panel.
    if (!this.closest('#filters-drawer, .cl-native-drawer') || this.details.open) return;
    this.#filterAnimation?.cancel();
    this.#contentAnimation?.cancel();
    this.#filterAnimation = null;
    this.#filterExpanded = false;
    this.summary.setAttribute('aria-expanded', 'false');
    this.details.style.removeProperty('height');
    this.details.style.removeProperty('overflow');
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    if (this.closest('#filters-drawer, .cl-native-drawer')) return;
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
